import { Component, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DataStateChangeEvent, GridComponent, GridDataResult } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { GridSettings } from 'src/app/core/model/grid-settings';
import { SessionSetting } from 'src/app/core/model/session-setting';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { ReportListService } from 'src/app/core/service/grid-data/grid-data-report.service';
import { ReportService } from 'src/app/core/service/report.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { ReportEdit } from './report-edit';
import { ExtractSaveEvent } from '../report-edit/report-edit.component';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';

@Component({
  standalone: false,
  selector: 'app-report-list',
  templateUrl: './report-list.component.html',
  styleUrls: ['./report-list.component.scss']
})
export class ReportListComponent implements OnInit
{
  @ViewChild(GridComponent) public grid: GridComponent;

  public pageSizes = [5, 10, 25, 50, 100];
  public deleteDialogOpened = false;
  public copyDialogOpened = false;
  public reportModalOpened = false;
  public reportViewName = '';
  public reportGridSettings: GridSettings;
  public reportId = undefined;
  public reportName = '';
  public enableAdvanced = false;
  public gridView: GridDataResult;
  public gridData: any = {};
  public count: number;
  public genders = [];
  public editDataItem: ReportEdit;
  public selectedReport: ReportEdit;
  public windowState = 'default';
  public reportTypes = null;

  /**
   * Control if Program Lead have disabled access to reporty
   */
  public reportEditDisabled = false;

  /**
   * Report list Kendo grid state.
   */
  public state: State = {
    skip: 0,
    filter: {
      logic: 'and',
      filters: []
    },
    sort: [
      { field: 'name', dir: 'asc' }
    ]
  };

  public filterText = '';
  public phoneNumberMask: string;
  public isNew = false;

  private copyText = '';
  private reportText = '';
  private metricText = '';

  constructor(
    private messengerService: MessengerService,
    private searchHelperService: SearchHelperService,
    private reportListService: ReportListService,
    private appConfigService: AppConfigService,
    private messageHandlerService: MessageHandlerService,
    private translate: TranslateService,
    private settingService: SettingService,
    private authenticationService: AuthenticationService,
    private reportService: ReportService
  )
  {
    this.state.take = this.appConfigService.getConfig().pageSize;
    this.reportTypes = this.reportService.reportTypes;
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.reportListService ? this.reportListService.loading : false;
  }

  public ngOnInit(): void
  {
    this.translate.get(['COPY', 'REPORT-TYPE-REPORT', 'REPORT-TYPE-METRIC']).subscribe({
      next: (res) =>
      {
        this.copyText = res.COPY;
        this.reportText = res['REPORT-TYPE-REPORT'];
        this.metricText = res['REPORT-TYPE-METRIC'];
        this.reportListService.subscribe({
          next: (value) =>
          {
            if (value !== null)
            {
              value.data.forEach(d =>
              {
                d.createdOn = new Date(d.createdOn);
                d.lastModifiedOn = new Date(d.lastModifiedOn);
              });
            }
            this.gridView = {
              data: (value) ? this.gridData = value.data : this.gridData = [],
              total: (value) ? this.count = value.total : this.count = 0
            };
          }
        });
        this.getReports();
      }
    });

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting: SessionSetting) =>
      {
        // Enforce Program Leads access to report
        if (sessionSetting.reportRestrictedUsers !== '')
        {
          const currentUser = this.authenticationService.currentUser;
          if (currentUser)
          {
            if (currentUser.role === 'ROLE_REFERRAL_COORDINATOR')
            {
              this.reportEditDisabled = true;
            }
            else if (currentUser.role === 'ROLE_PROGRAM_LEAD' || currentUser.role === 'ROLE_PROGRAM_SUPPORT')
            {
              const restrictedUsers = sessionSetting.reportRestrictedUsers.split(',');             
              restrictedUsers.every((id: string) =>
              {
                if (parseInt(id, 10) === currentUser.id)
                {
                  this.reportEditDisabled = true;
                }
                return !this.reportEditDisabled;  // Stop iteration when set to false
              });
            }
          }
          else
          {
            this.reportEditDisabled = true;
          }
        }
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public searchFieldChange(filterText: string): void
  {
    this.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.reportListService.queryByState(state);
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.state.filter = filter;
    this.reportListService.queryByState(this.state);
  }

  /**
   * Add report handler
   */
  public addHandler(): void
  {
    this.isNew = true;
    setTimeout(() =>
    {
      this.editDataItem = new ReportEdit();
    });
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Save report handler.
   *
   * @param report Report object to save.
   */
  public saveChangesHandler(report: ExtractSaveEvent): void
  {
    this.reportService.saveReport(report.data, this.isNew, report.data.id).subscribe({
      next: (data) =>
      {
        this.messageHandlerService.displaySuccess('REPORT-SAVED-SUCCESSFULLY');
        this.isNew = false;
        this.editDataItem = report.apply ? data : undefined;
        this.getReports();

        if (report.apply)
        {
          this.messengerService.sendMessage(new MessageSubject('afterExtractSaveAndApply'));
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Cancel report changes.
   */
  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  /**
   * Edit handler
   *
   * @param id Report id.
   */
  public editHandler(id: number): void
  {
    this.isNew = false;
    this.reportService.getReportById(id).subscribe({
      next: (data) =>
      {
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public confirmHandler(id: number, dialogName: string): void
  {
    if (dialogName === 'deleteDialog')
    {
      this.reportService.existsReport(id).subscribe({
        next: (inUseResponse) =>
        {
          // If in use, avoid deletion
          if (inUseResponse)
          {
            this.messageHandlerService.displayWarning('METRIC-IN-USE-CANNOT-DELETE');
          }
          else
          {
            this.performDialogAction(id, dialogName);
          }
        }
      });
    }
    else
    {
      this.performDialogAction(id, dialogName);
    }
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      const reportId = this.selectedReport.id;
      this.reportService.deleteReport(reportId).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('REPORT-DELETED-SUCCESSFULLY');
          this.getReports();
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    // Closing dialog
    this.close('deleteDialog');
    this.selectedReport = null;
  }

  public runReport(dataItem: any): void
  {
    this.closeReport();
    setTimeout(() =>
    {
      this.reportId = dataItem.id;
      this.reportName = dataItem.name;
      this.reportViewName = dataItem.sqlViewName;
      this.reportGridSettings =
      {
        state: JSON.parse(dataItem.sqlViewFilter.state),
        columnsConfig: JSON.parse(dataItem.sqlViewFilter.columns),
        filterFunctions: dataItem.sqlViewFilter.filterFunctions
      };
      this.open('reportModal');
    });
  }

  public closeReport(): void
  {
    this.close('reportModal');
    this.reportViewName = '';
    this.reportGridSettings = null;
  }

  /**
   * Copy report definition
   *
   * @param confirm True when the copy record data is confirmed
   */
  public copyAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Reset both ids to create new records
      this.selectedReport.id = null;
      this.selectedReport.sqlViewFilter.id = null;
      this.selectedReport.name = `${this.selectedReport.name} - ${this.copyText}`;
      this.selectedReport.sqlViewFilter.name = this.selectedReport.name;
      this.reportService.saveReport(this.selectedReport, true).subscribe({
        next: () =>
        {
          this.editDataItem = undefined;
          this.messageHandlerService.displaySuccess('REPORT-SAVED-SUCCESSFULLY');
          this.getReports();

          // Closing dialog
          this.close('copyDialog');
          this.selectedReport = null;
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    else
    {
      // Closing dialog
      this.close('copyDialog');
    }
  }

  public onWindowStateChange(state: string): void
  {
    this.windowState = state;
  }

  /**
   * Get reports from API per the applied filter.
   *
   * @param filter Task filter string.
   */
  private getReports(): void
  {
    this.state.skip = 0;
    this.reportListService.queryByState(this.state);
  }

  /**
   * Either open the delete or copy dialog confirmation.
   *
   * @param id Extract id
   * @param dialogName Dialog name
   */
  private performDialogAction(id: number, dialogName: string): void
  {
    this.reportService.getReportById(id).subscribe({
      next: (data) =>
      {
        this.selectedReport = data as ReportEdit;
        this.open(dialogName);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }
}
